<script>
    import { preventDefault } from 'svelte/legacy';

    import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
    import { projectState } from "@app/store/projectStore.svelte.js";
    import { modalState, hideModal } from "@app/store/modalStore.svelte.js";
    import { fade } from "svelte/transition";
    import { toast } from "@zerodevx/svelte-toast";
    import { insertHistoryAction } from "@app/store/historyStore.svelte.js";
    import _FrameRates from "@cclib/dict/frameRates.js";
    import tcLib from '@cclib/lib/timecode.js'
    import incodeTime from '@cclib/lib/incodeTime.js'
    import * as m from '@app/paraglide/messages.js';
    
    let clockTimes = $state("maintain"); //modify, or maintain
    let srcFrameRate = $state(projectState.frameRate.toString());
    let trgFrameRate = $state(projectState.frameRate.toString());
    let srcDropFrame = $state(projectState.dropFrame ? "true" : "false");
    let trgDropFrame = $state(projectState.dropFrame ? "true" : "false");
    let applyAll = $state(false);

    let frameRateCalcMap = {
        "23.976": "23.976",
        "24": "24",
        "25": "25",
        "29.97": "29.97",
        "30": "30",
        "50" : "25",
        "59.94": "29.97",
        "60": "30",
    };

    // Computed values for preview
    let previewData = $derived.by(() => {
        const currentEventGroup = eventGroupState[projectState.selected];
        if (!currentEventGroup || !currentEventGroup.events || currentEventGroup.events.length === 0) {
            return null;
        }

        const firstEvent = currentEventGroup.events[0];
        const lastEvent = currentEventGroup.events[currentEventGroup.events.length - 1];
        
        const srcFR = parseFloat(srcFrameRate);
        const trgFR = parseFloat(trgFrameRate);
        const srcDF = srcDropFrame === "true";
        const trgDF = trgDropFrame === "true";
        
        let incode = projectState.incode;    
        
        let afterFirstSec, afterLastSec, beforeFirstSec, beforeLastSec;
        
        // Calculate before values (current)
        const beforeFirst = tcLib.secToTc(firstEvent.start+incode, srcFR, srcDF);
        const beforeLast = tcLib.secToTc(lastEvent.start+incode, srcFR, srcDF);
        beforeFirstSec = firstEvent.start;
        beforeLastSec = lastEvent.start;
        
        // Calculate after values based on clockTimes setting      
        if (clockTimes === "modify") {
            const srcFrameRateCalc = frameRateCalcMap[srcFrameRate];
            const trgFrameRateCalc = frameRateCalcMap[trgFrameRate];
            afterFirstSec = incode + (firstEvent.start * (parseFloat(srcFrameRateCalc) / parseFloat(trgFrameRateCalc)));
            afterLastSec = incode + (lastEvent.start * (parseFloat(srcFrameRateCalc) / parseFloat(trgFrameRateCalc)));
        } else {
            // maintain - just change the representation
            afterFirstSec = incode + firstEvent.start;
            afterLastSec = incode + lastEvent.start;
        }
        
        const afterFirst = tcLib.secToTc(afterFirstSec, trgFR, trgDF);
        const afterLast = tcLib.secToTc(afterLastSec, trgFR, trgDF);
        afterFirstSec-= incode;
        afterLastSec-=incode;
        
        return {
            beforeFirst,
            beforeFirstSec,
            beforeLast,
            beforeLastSec,
            afterFirst,
            afterFirstSec,
            afterLast,
            afterLastSec
        };
    });

    function updateSrcDropFrame(){
        if (["29.97", "59.94"].indexOf(srcFrameRate) > -1){
            srcDropFrame = "true";
        } else {
            srcDropFrame = "false";
        }
    }

    function updateTrgDropFrame(){
        if (["29.97", "59.94"].indexOf(trgFrameRate) > -1){
            trgDropFrame = "true";
        } else {
            trgDropFrame = "false";
        }
    }

    function convertTimecode(){
       try {
            if (clockTimes === "modify"){
                let srcFrameRateCalc = frameRateCalcMap[srcFrameRate];
                let trgFrameRateCalc = frameRateCalcMap[trgFrameRate];

                eventGroupState.forEach((evg, evgIndex) =>{
                    if (applyAll || evgIndex === projectState.selected){
                        evg.events.forEach((event, index, events) => {
                            events[index].start = event.start * (parseFloat(srcFrameRateCalc) / parseFloat(trgFrameRateCalc));
                            events[index].end = event.end * (parseFloat(srcFrameRateCalc) / parseFloat(trgFrameRateCalc));
                        });
                    }
                });
                
            }

            const trgFR = parseFloat(trgFrameRate);
            const trgDF = trgDropFrame === "true";
            projectState.frameRate = trgFR;
            projectState.dropFrame = trgDF;

            const incodeTc = incodeTime.mediaSecondsToIncodeLabel(projectState.incode, trgFR, trgDF);
            projectState.incode = incodeTime.incodeLabelToMediaSeconds(incodeTc, trgFR, trgDF);

            insertHistoryAction({
                name: "convert timecode", //action name
                eventGroup: projectState.selected,
                snapshots: [
                    {
                        store: "projectState",
                        value: JSON.stringify(projectState),
                    },
                    {
                        store: "eventGroupState",
                        value: JSON.stringify(eventGroupState)
                    },
                ],
            });

            toast.push(m.timecode_conversion_complete(), {
                classes: ["toast-success"],
            });
       } catch(err){
            toast.push(m.timecode_conversion_failed(), {
                classes: ["toast-error"],
            });

            console.log(err.message, err);
       } finally {
            hideModal();
       }        
    }
</script>

<div
    transition:fade={{ duration: 100 }}
    class="modal {modalState.selected === 'convertTimecode' ? 'show d-block' : ''}"
    role="dialog"
    aria-labelledby="convertTimecodeTitle"
    aria-describedby="convertTimecodeDescription"
    tabindex="-1"
    id="ConvertTimecodeModal"
>
    <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="convertTimecodeTitle">{m.convert_timecode()}</h4>
                <button
                    type="button"
                    class="btn-close"
                    aria-label={m.close()}
                    id="closeConvertTimecodeBtn"
                    onclick={hideModal}
                ></button>
            </div>
            <div class="modal-body">
                <p id="convertTimecodeDescription" class="sr-only">{m.convert_timecode_description()}</p>
                <form onsubmit={preventDefault(convertTimecode)} id="ConvertTimecodeForm">
                    <!-- Radio buttons for user to set clockTimes to modify or maintain   -->
                     <div class="row">
                        <div class="col-12 mb-3">
                            <fieldset>
                                <legend class="sr-only">{m.clock_times_option()}</legend>
                                <div class="form-check">
                                    <input 
                                        class="form-check-input" 
                                        type="radio" 
                                        name="clockTimesRadios" 
                                        id="maintainClockTimesCheck" 
                                        value="maintain" 
                                        bind:group={clockTimes}
                                        aria-describedby="maintainClockTimesHelp"
                                    >
                                    <label class="form-check-label" for="maintainClockTimesCheck">
                                                                            {m.maintain_clock_times()} 
                                    </label>
                                                                        <span id="maintainClockTimesHelp" class="visually-hidden">{m.maintain_clock_times_help()}</span>
                                </div>
                                <div class="form-check">
                                    <input 
                                        class="form-check-input" 
                                        type="radio" 
                                        name="clockTimesRadios" 
                                        id="modifyClockTimesCheck" 
                                        value="modify" 
                                        bind:group={clockTimes}
                                        aria-describedby="modifyClockTimesHelp"
                                    >
                                    <label class="form-check-label" for="modifyClockTimesCheck">
                                                                            {m.modify_clock_times()} 
                                    </label>
                                                                        <span id="modifyClockTimesHelp" class="visually-hidden">{m.modify_clock_times_help()}</span>
                                </div>
                            </fieldset>
                        </div>
                        <div class="col-6 mb-3">
                            <!-- Select List of From and To -->
                                                        <label class="form-label" for="srcFrameRateSelector">{m.source_frame_rate()}</label>
                            <select 
                                id="srcFrameRateSelector" 
                                class="form-select" 
                                onchange={updateSrcDropFrame} 
                                bind:value={srcFrameRate}
                                aria-describedby="srcFrameRateHelp"
                            >
                                <option value="23.976">23.976</option>
                                <option value="24">24</option>
                                <option value="25">25</option>
                                <option value="29.97">29.97</option>
                                <option value="30">30</option>
                                <option value="59.94">59.94</option>
                                <option value="60">60</option>
                            </select>
                            <span id="srcFrameRateHelp" class="visually-hidden">{m.source_frame_rate_help()}</span>
                        </div>
                        <div class="col-6 mb-3">
                            <!-- Select List of From and To -->
                            <label class="form-label" for="trgFrameRateSelector">{m.target_frame_rate()}</label>
                            <select 
                                id="trgFrameRateSelector" 
                                class="form-select" 
                                onchange={updateTrgDropFrame} 
                                bind:value={trgFrameRate}
                                aria-describedby="trgFrameRateHelp"
                            >
                                <option value="23.976">23.976</option>
                                <option value="24">24</option>
                                <option value="25">25</option>
                                <option value="29.97">29.97</option>
                                <option value="30">30</option>
                                <option value="59.94">59.94</option>
                                <option value="60">60</option>
                            </select>
                            <span id="trgFrameRateHelp" class="visually-hidden">{m.target_frame_rate_help()}</span>
                        </div>

                        {#if ["29.97", "59.94"].indexOf(srcFrameRate) > -1}
                        <div class="col-6 mb-3">
                            <label class="form-label" for="srcDropFrameSelector">{m.source_drop_frame()}</label>
                            <select id="srcDropFrameSelector" class="form-select" bind:value={srcDropFrame}>
                                <option value="true">{m.common_true()}</option>
                                <option value="false">{m.common_false()}</option>
                            </select>
                        </div>
                        {/if}
                        {#if ["29.97", "59.94"].indexOf(trgFrameRate) > -1}
                        <div class="col-6 mb-3">
                            <label class="form-label" for="trgDropFrameSelector">{m.target_drop_frame()}</label>
                            <select id="trgDropFrameSelector" class="form-select" bind:value={trgDropFrame}>
                                <option value="true">{m.common_true()}</option>
                                <option value="false">{m.common_false()}</option>
                            </select>
                        </div>
                        {/if}
                     </div>
                    <div class="mb-3 col-6">
                        <div class="form-check">
                            <input class="form-check-input" type="checkbox" id="applyToAllChecked" bind:checked={applyAll}>
                            <label class="form-check-label" for="applyToAllChecked">
                                                            {m.apply_to_all_event_groups()}<i class="bi bi-info-circle" title={m.apply_to_all_event_groups_help()}></i>
                            </label>
                          </div>
                    </div>

                    <!-- Preview of Changes Section -->
                    {#if previewData && (srcFrameRate !== trgFrameRate || srcDropFrame !== trgDropFrame)}
                    
                    <div class="mb-3">
                        <hr/>
                        <h6 class="fw-bold">{m.preview_changes()}</h6>
                        <div class="table-responsive">
                            <table class="table table-sm table-bordered">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th class="text-center">{m.before()}</th>
                                        <th class="text-center">{m.after()}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>{m.first_caption()}</td>
                                        <td class="text-center font-monospace">{previewData.beforeFirst} ({previewData.beforeFirstSec.toFixed(3)}s)</td>
                                        <td class="text-center font-monospace">{previewData.afterFirst} ({previewData.afterFirstSec.toFixed(3)}s)</td>
                                    </tr>
                                    <tr>
                                        <td>{m.last_caption()}</td>
                                        <td class="text-center font-monospace">{previewData.beforeLast} ({previewData.beforeLastSec.toFixed(3)}s)</td>
                                        <td class="text-center font-monospace">{previewData.afterLast} ({previewData.afterLastSec.toFixed(3)}s)</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    {/if}

                    <button 
                        class="btn btn-primary float-end" 
                        type="submit" 
                        id="convertTimecodeSubmit"
                        aria-label={m.convert_timecode()}
                    >{m.convert()}</button>
                </form>
            </div>
        </div>
    </div>
</div>

<style>
    .table {
        margin-bottom: 0;
    }
    
    .table td:first-child {
        width: 120px;
        font-weight: 500;
    }
    
    .font-monospace {
        font-size: 0.9rem;
    }
</style>